import {
  Alert,
  AlertDescription,
  Button,
  Card,
  CardContent,
  Input,
  Label,
  LoadingState,
  toast,
} from '@sitewise/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';


import { fetchOrg, updateOrgBranding } from '../../../api/org.js';
import { ApiError } from '../../../lib/api-client.js';
import { useBrandingStore } from '../../../stores/branding-store.js';
import { useSyncStore } from '../../../stores/sync-store.js';

export function OrgBrandingPage() {
  const queryClient = useQueryClient();
  const apply = useBrandingStore((s) => s.apply);
  const track = useSyncStore((s) => s.track);
  const orgQuery = useQuery({ queryKey: ['org'], queryFn: fetchOrg });

  const [name, setName] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [color, setColor] = useState('#1e40af');

  useEffect(() => {
    if (orgQuery.data) {
      setName(orgQuery.data.org.name);
      setLogoUrl(orgQuery.data.org.logoUrl ?? '');
      setColor(orgQuery.data.org.primaryColorHex ?? '#1e40af');
    }
  }, [orgQuery.data]);

  const mutation = useMutation({
    mutationFn: () =>
      track(
        updateOrgBranding({
          name,
          logoUrl: logoUrl ? logoUrl : null,
          primaryColorHex: color || null,
        }),
      ),
    onSuccess: (data) => {
      toast.success('Branding updated');
      queryClient.invalidateQueries({ queryKey: ['org'] });
      queryClient.invalidateQueries({ queryKey: ['public-branding'] });
      apply({
        orgName: data.org.name,
        orgSlug: data.org.slug,
        primaryColorHex: data.org.primaryColorHex,
        logoUrl: data.org.logoUrl,
      });
    },
  });

  if (orgQuery.isPending) return <LoadingState rows={3} />;
  if (orgQuery.isError || !orgQuery.data) {
    return (
      <Alert variant="danger">
        <AlertDescription>Could not load organization.</AlertDescription>
      </Alert>
    );
  }

  return (
    <Card>
      <CardContent className="space-y-4 pt-6">
        <header>
          <h2 className="text-lg font-semibold">Branding</h2>
          <p className="text-sm text-fg-secondary">
            The logo and color you set here appear in this app, your login page, and email
            notifications.
          </p>
        </header>

        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            mutation.mutate();
          }}
          noValidate
        >
          <div className="space-y-1.5">
            <Label htmlFor="org-name">Organization name</Label>
            <Input
              id="org-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="org-logo">Logo URL</Label>
            <Input
              id="org-logo"
              type="url"
              placeholder="https://…"
              value={logoUrl}
              onChange={(e) => setLogoUrl(e.target.value)}
            />
            <p className="text-xs text-fg-muted">
              Phase 0 takes a public URL. Phase 1 swaps in direct uploads via Spaces.
            </p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="org-color">Primary color</Label>
            <div className="flex items-center gap-3">
              <input
                id="org-color"
                type="color"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                className="h-9 w-12 cursor-pointer rounded border border-border bg-surface"
              />
              <Input
                value={color}
                onChange={(e) => setColor(e.target.value)}
                pattern="^#[0-9a-fA-F]{6}$"
                className="font-mono"
              />
              <div
                className="h-9 w-24 rounded text-sm font-medium flex items-center justify-center"
                style={{ backgroundColor: color, color: '#fff' }}
              >
                Preview
              </div>
            </div>
          </div>

          {mutation.error ? (
            <Alert variant="danger">
              <AlertDescription>
                {mutation.error instanceof ApiError
                  ? mutation.error.message
                  : 'Could not update branding.'}
              </AlertDescription>
            </Alert>
          ) : null}

          <div className="flex justify-end">
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? 'Saving…' : 'Save branding'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
